<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { z } from 'zod';

const { t } = useI18n({
  useScope: 'local',
});

const editStore = useHousingEditStore();
const { housing } = storeToRefs(editStore);
const { showToast } = useToast();
const modal = ref();
const isSavingData = ref(false);
const minimumCharacters = 150;

const emit = defineEmits(['updated']);

const { defineField, handleSubmit, errors } = useForm({
  validationSchema: toTypedSchema(
    z.object({
      description: z
        .string()
        .trim()
        .min(10, { message: t('at-least-n-chars', { field: t('description'), n: 10 }) })
        .default(housing.value!.description),
    })
  ),
});

const [description, descriptionAttrs] = defineField('description');
const descriptionLength = computed(() => description.value ? description.value.length : 0);

const onSubmit = handleSubmit(async (values) => {
  isSavingData.value = true;
  try {
    const update = await editStore.update({ ...housing.value!, ...values });
    emit('updated', update);
    showToast('success', t('success'), t('changes-saved-successfully'));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_error) {
    showToast('error', t('error'), t('error-saving-changes'));
  } finally {
    isSavingData.value = false;
    modal.value?.close();
  }
});
</script>

<template>
  <tw-housing-edit-modal class="modal" ref="modal" @submit="onSubmit" :is-saving-data="isSavingData" type="description">
    <template #title>
      <span>{{ t('to-edit-description', 1) }}</span>
    </template>
    <div class="content">
      <label for="description">{{ t('description', 1) }} - {{ t('character') }}: <span>{{  descriptionLength }}</span></label>
      <textarea
        rows="5"
        required
        id="description"
        name="description"
        v-model="description"
        v-bind="descriptionAttrs"
        :class="['tw-housing-edit-modal-description', { error: errors.description }, { formError: errors.description || descriptionLength < minimumCharacters }]"
        :placeholder="t('to-edit-description', 1)"
      />
      <tw-form-error v-if="errors.description">{{ errors.description }}</tw-form-error>
      <tw-form-error v-if="descriptionLength < minimumCharacters">{{ t('description-should-be-x-chars-long', { n: minimumCharacters} ) }}</tw-form-error>
    </div>
  </tw-housing-edit-modal>
</template>

<style scoped lang="scss">
@import '~/assets/styles/tw-variables.scss';
@import '~/assets/styles/tw-mixins.scss';
@import '~/assets/styles/tw-form.scss';

.content {
  gap: 0.5rem;
  display: flex;
  flex-direction: column;
}

textarea {
  white-space: pre-wrap;
  @include tw-input-string;
}

.character-hint {
  font-size: 0.9rem;
  color: $neutral-80;
}

.formError {
  border: 1px solid $error;
}
</style>

<i18n lang="json">
{
  "de": {
    "description": "Beschreibung",
    "to-edit-description": "Beschreibung bearbeiten | Beschreibungen bearbeiten",
    "description-should-be-x-chars-long": "Deine Beschreibung sollte mindestens {n} Zeichen lang sein.",
    "character": "Zeichen",
    "at-least-n-chars": "{field} muss mindestens {n} Zeichen enthalten."
  },
  "en": {
    "description": "Description",
    "to-edit-description": "Edit description | Edit descriptions",
    "description-should-be-x-chars-long": "Your description should be at least {n} characters long.",
    "character": "Character",
    "at-least-n-chars": "{field} must contain at least {n} characters."
  }
}
</i18n>
